import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Mic2,
  FileAudio,
  TrendingUp,
  BarChart3,
  Wallet,
  CreditCard,
  Settings,
  HelpCircle,
  Users,
  UsersRound,
  
  Search,
  Shield,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useAuth } from "@/contexts/AuthContext";

interface SearchItem {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  category: "navigation" | "content" | "admin";
  keywords?: string[];
}

const navigationItems: SearchItem[] = [
  {
    id: "dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
    category: "navigation",
    keywords: ["home", "overview"],
  },
  {
    id: "shows",
    title: "Shows",
    icon: Mic2,
    path: "/shows",
    category: "navigation",
    keywords: ["podcasts", "programs"],
  },
  {
    id: "episodes",
    title: "Episodes",
    icon: FileAudio,
    path: "/episodes",
    category: "navigation",
    keywords: ["audio", "content"],
  },
  {
    id: "upload",
    title: "Upload Episode",
    icon: FileAudio,
    path: "/episodes/upload",
    category: "navigation",
    keywords: ["new", "add", "create"],
  },
  {
    id: "analytics",
    title: "Analytics",
    icon: BarChart3,
    path: "/analytics",
    category: "navigation",
    keywords: ["stats", "metrics", "data"],
  },
  {
    id: "revenue",
    title: "Earnings & Payouts",
    icon: Wallet,
    path: "/revenue",
    category: "navigation",
    keywords: ["revenue", "earnings", "payouts", "money", "income"],
  },
  {
    id: "wallet",
    title: "Wallet",
    icon: Wallet,
    path: "/wallet",
    category: "navigation",
    keywords: ["balance", "payout", "withdraw"],
  },
  {
    id: "revenue-split",
    title: "Revenue Splits",
    icon: Users,
    path: "/revenue-split",
    category: "navigation",
    keywords: ["collaborators", "share"],
  },
  {
    id: "team",
    title: "Team",
    icon: UsersRound,
    path: "/team",
    category: "navigation",
    keywords: ["members", "invite"],
  },
  {
    id: "billing",
    title: "Billing",
    icon: CreditCard,
    path: "/billing",
    category: "navigation",
    keywords: ["subscription", "payment", "plan"],
  },
  {
    id: "settings",
    title: "Settings",
    icon: Settings,
    path: "/settings",
    category: "navigation",
    keywords: ["preferences", "account", "profile"],
  },
  {
    id: "help",
    title: "Help & Support",
    icon: HelpCircle,
    path: "/help",
    category: "navigation",
    keywords: ["support", "contact", "faq"],
  },
];

const adminItems: SearchItem[] = [
  {
    id: "admin-dashboard",
    title: "Admin Dashboard",
    icon: Shield,
    path: "/admin",
    category: "admin",
    keywords: ["admin", "overview"],
  },
  {
    id: "admin-users",
    title: "Manage Users",
    icon: Users,
    path: "/admin/users",
    category: "admin",
    keywords: ["admin", "accounts"],
  },
  {
    id: "admin-podcasts",
    title: "All Podcasts",
    icon: Mic2,
    path: "/admin/podcasts",
    category: "admin",
    keywords: ["admin", "shows"],
  },
  {
    id: "admin-approval-queue",
    title: "Approval Queue",
    icon: Shield,
    path: "/admin/approval-queue",
    category: "admin",
    keywords: ["admin", "approve", "review", "queue"],
  },
  {
    id: "admin-support",
    title: "Support Tickets",
    icon: HelpCircle,
    path: "/admin/support",
    category: "admin",
    keywords: ["admin", "tickets"],
  },
  {
    id: "admin-settings",
    title: "Admin Settings",
    icon: Settings,
    path: "/admin/settings",
    category: "admin",
    keywords: ["admin", "config"],
  },
];

// Routes a network admin (non-platform-admin) is allowed to navigate to.
const NETWORK_ADMIN_ALLOWED = new Set([
  "/admin/podcasts",
  "/admin/approval-queue",
  "/admin/users",
]);

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalSearch({ open, onOpenChange }: GlobalSearchProps) {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isPlatformAdmin = hasRole("admin");
  const isNetworkAdmin = hasRole("network_admin");
  const visibleAdminItems = isPlatformAdmin
    ? adminItems
    : isNetworkAdmin
      ? adminItems.filter((i) => NETWORK_ADMIN_ALLOWED.has(i.path))
      : [];
  const visibleNavigationItems = isPlatformAdmin
    ? [
        ...navigationItems,
        {
          id: "delivery",
          title: "Delivery Status",
          icon: TrendingUp,
          path: "/delivery",
          category: "navigation" as const,
          keywords: ["distribution", "platforms"],
        },
      ]
    : navigationItems;

  const handleSelect = useCallback(
    (path: string) => {
      onOpenChange(false);
      navigate(path);
    },
    [navigate, onOpenChange]
  );

  // Keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search pages, shows, episodes..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        
        <CommandGroup heading="Navigation">
          {visibleNavigationItems.map((item) => (
            <CommandItem
              key={item.id}
              value={`${item.title} ${item.keywords?.join(" ") || ""}`}
              onSelect={() => handleSelect(item.path)}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.title}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {visibleAdminItems.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Admin">
              {visibleAdminItems.map((item) => (
                <CommandItem
                  key={item.id}
                  value={`${item.title} ${item.keywords?.join(" ") || ""}`}
                  onSelect={() => handleSelect(item.path)}
                >
                  <item.icon className="mr-2 h-4 w-4" />
                  <span>{item.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}

// Hook for managing global search state
export function useGlobalSearch() {
  const [open, setOpen] = useState(false);
  return { open, setOpen };
}
